import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle, AlertCircle, MapPin, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { oapi, fmtWhen } from '@/lib/officialsApi';
import { PublicShell } from './OfficialsJoin';

const GameDetails = ({ a }) => {
  const g = a.game || {};
  return (
    <div className="rounded-2xl bg-slate-50 border p-4 text-left space-y-1.5" data-testid="respond-game-details">
      <div className="text-xs font-bold uppercase tracking-wide text-[#CF142B]">{a.role_label}</div>
      <div className="text-xl font-black text-slate-900 leading-tight" style={{ fontFamily: 'Barlow Condensed' }}>{g.team_a_name} vs {g.team_b_name}</div>
      <div className="text-sm text-slate-600 flex items-center gap-1.5"><CalendarDays className="w-4 h-4" />{fmtWhen(g)}</div>
      <div className="text-sm text-slate-600 flex items-center gap-1.5"><MapPin className="w-4 h-4" />{[g.venue || 'Venue TBD', g.court && `Court ${g.court}`].filter(Boolean).join(' · ')}</div>
      <div className="text-xs text-slate-400">{[g.event_name, a.org_name].filter(Boolean).join(' · ')}</div>
      {a.instructions && <div className="text-sm bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-amber-900">{a.instructions}</div>}
    </div>
  );
};

const Outcome = ({ icon: Icon, tone, title, text, testId }) => (
  <div className="text-center" data-testid={testId}>
    <Icon className={`w-12 h-12 mx-auto ${tone}`} />
    <h1 className="mt-3 text-2xl font-black uppercase text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>{title}</h1>
    {text && <p className="text-sm text-slate-600 mt-1">{text}</p>}
  </div>
);

export default function OfficialsRespond() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const action = params.get('action');
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [status, setStatus] = useState(null);
  const [declining, setDeclining] = useState(action === 'decline');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const auto = useRef(false);

  const respond = useCallback(async (act, why = '') => {
    setBusy(true);
    try {
      const r = await oapi(`/officials/respond/${token}`, { method: 'POST', body: { action: act, reason: why } });
      setStatus(r.status);
      setDeclining(false);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }, [token]);

  useEffect(() => {
    oapi(`/officials/respond/${token}`).then(x => {
      setD(x);
      setStatus(x.assignment.status);
      if (action === 'accept' && x.can_respond && x.assignment.status === 'pending' && !auto.current) { auto.current = true; respond('accept'); }
    }).catch(e => setErr(e.message));
  }, [token, action, respond]);

  if (err) {
    return (
      <PublicShell>
        <div className="bg-white rounded-3xl p-8 space-y-4">
          <Outcome icon={AlertCircle} tone="text-red-500" title="Link not valid" text={err} testId="respond-error" />
          <Link to="/officials/portal" className="block text-center text-sm font-semibold text-[#00247D] hover:underline" data-testid="respond-error-portal-link">Open the officials portal</Link>
        </div>
      </PublicShell>
    );
  }
  if (!d) return <PublicShell><Loader2 className="w-8 h-8 animate-spin text-white mx-auto" /></PublicShell>;
  const a = d.assignment;
  const open = d.can_respond && ['pending', 'accepted'].includes(status);

  return (
    <PublicShell>
      <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-2xl space-y-5" data-testid="respond-card">
        {busy && !declining ? <Loader2 className="w-8 h-8 animate-spin text-[#00247D] mx-auto" />
          : status === 'accepted' ? <Outcome icon={CheckCircle2} tone="text-emerald-500" title="You're confirmed" text={`Thanks ${a.official_first_name || ''}! A reminder email comes before the game.`} testId="respond-accepted" />
            : status === 'declined' ? <Outcome icon={XCircle} tone="text-red-500" title="Assignment declined" text="Your assigner has been told." testId="respond-declined" />
              : status === 'cancelled' ? <Outcome icon={AlertCircle} tone="text-slate-400" title="This assignment was cancelled" testId="respond-cancelled" />
                : !d.can_respond ? <Outcome icon={AlertCircle} tone="text-slate-400" title="This game has finished" testId="respond-finished" />
                  : <Outcome icon={CalendarDays} tone="text-[#00247D]" title={`Hi ${a.official_first_name || 'there'}, can you officiate?`} text={`${a.org_name} assigned you to this game.`} testId="respond-pending" />}
        <GameDetails a={a} />
        {open && declining && (
          <div className="space-y-3" data-testid="respond-decline-form">
            <Textarea rows={3} value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason (optional)" data-testid="respond-decline-reason" />
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setDeclining(false)}>Back</Button>
              <Button className="flex-1 bg-red-600 hover:bg-red-700" disabled={busy} onClick={() => respond('decline', reason)} data-testid="respond-decline-confirm">
                {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}Decline
              </Button>
            </div>
          </div>
        )}
        {open && !declining && !busy && (
          <div className="flex gap-2">
            {status === 'pending' && <Button className="flex-1 h-11 bg-emerald-600 hover:bg-emerald-700" onClick={() => respond('accept')} data-testid="respond-accept-btn">Accept</Button>}
            <Button variant="outline" className="flex-1 h-11 text-red-600 border-red-200 hover:bg-red-50" onClick={() => setDeclining(true)} data-testid="respond-decline-btn">
              {status === 'accepted' ? "I can't make it" : 'Decline'}
            </Button>
          </div>
        )}
        <p className="text-center text-xs text-slate-500">
          See all your games in the <Link to="/officials/portal" className="font-semibold text-[#00247D] hover:underline" data-testid="respond-portal-link">officials portal</Link>
        </p>
      </div>
    </PublicShell>
  );
}
